import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FaCloudUploadAlt } from 'react-icons/fa';
import axios from 'axios';

const ImportMedicines = () => {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Function to generate dynamic sample data
  const generateSampleData = () => {
    const sampleData = [];
    const categories = ['Tablet', 'Capsule', 'Syrup'];
    const units = ['Tablet', 'ml', 'Capsule'];
    const groups = ['Analgesic', 'Antibiotic', 'Antiviral'];
    const companies = ['Pharma Co 1', 'Pharma Co 2', 'Pharma Co 3'];

    for (let i = 1; i <= 2; i++) {
      sampleData.push({
        medicineName: `Medicine ${i}`,
        medicineCategory: categories[i % categories.length],
        medicineCompany: companies[i % companies.length],
        medicineGroup: groups[i % groups.length],
        unit: units[i % units.length],
        vat: 5.0 + i,
        rackNumber: `R${i}`,
        note: `Store below ${20 + i}°C`,
      });
    }
    return sampleData;
  };

  const sampleData = generateSampleData();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error('Please upload a file');
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post('https://api.muktihospital.com/api/medicine-import', formData, {
        headers: { 'x-api-key': 'caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079' },
      });
      toast.success(response.data.message);
      setFile(null);
      document.querySelector('input[type="file"]').value = null; // Reset file input
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to import medicines');
    } finally {
      setIsLoading(false);
    }
  };

  const downloadSample = () => {
    const headers = [
      'medicineName',
      'medicineCategory',
      'medicineCompany',
      'medicineGroup',
      'unit',
      'vat',
      'rackNumber',
      'note',
    ];

    const csvContent = [
      headers.join(','),
      ...sampleData.map(row =>
        headers.map(header => `"${row[header] || ''}"`).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_medicine_data.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
          <h1 className="text-2xl font-bold text-gray-700">Import Medicines</h1>
          <button
            onClick={downloadSample}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-500 text-white hover:bg-blue-600 text-sm"
          >
            <FaCloudUploadAlt size={16} />
            Download Sample Data
          </button>
        </div>

        <div className="mb-6">
          <p className="text-sm text-gray-600">
            <span className="font-bold">Note:</span> Your file (CSV, Excel, or JSON) must match the format below. For CSV files, the first line should include column headers as shown. Use UTF-8 encoding to avoid issues. Ensure Medicine Name, Category, Company, Group, and Unit are provided and match existing database values. The table below shows sample data for reference.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-700 p-2 pr-0">
                  Medicine <span className="text-red-500">*</span>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-700 p-1">
                  Category <span className="text-red-500">*</span>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-700 p-1">
                  Company <span className="text-red-500">*</span>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-700 p-1">
                  Group <span className="text-red-500">*</span>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-700 p-1">
                  Unit <span className="text-red-500">*</span>
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-700 p-1">
                  VAT
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-700 p-1">
                  Rack Number
                </th>
                <th className="text-left uppercase font-inter font-medium text-sm text-gray-700 p-2">
                  Note
                </th>
              </tr>
            </thead>
            <tbody>
              {sampleData.map((row, index) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="text-left capitalize font-inter font-normal text-sm text-gray-700 py-5 px-2">
                    {row.medicineName}
                  </td>
                  <td className="text-left capitalize font-inter font-normal text-sm text-gray-700 py-5 px-1">
                    {row.medicineCategory}
                  </td>
                  <td className="text-left capitalize font-inter font-normal text-sm text-gray-700 py-5 px-1">
                    {row.medicineCompany}
                  </td>
                  <td className="text-left capitalize font-inter font-normal text-sm text-gray-700 py-5 px-1">
                    {row.medicineGroup}
                  </td>
                  <td className="text-left capitalize font-inter font-normal text-sm text-gray-700 py-5 px-1">
                    {row.unit}
                  </td>
                  <td className="text-left capitalize font-inter font-normal text-sm text-gray-700 py-5 px-1">
                    {row.vat}
                  </td>
                  <td className="text-left capitalize font-inter font-normal text-sm text-gray-700 py-5 px-1">
                    {row.rackNumber}
                  </td>
                  <td className="text-left capitalize font-inter font-normal text-sm text-gray-700 py-5 px-2">
                    {row.note}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mt-5 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select File <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".csv,.xlsx,.xls,.json"
                onChange={handleFileChange}
                aria-label="Upload CSV, Excel, or JSON file"
                className="w-full p-2 border rounded-md outline-none focus:ring-0 text-sm opacity-0 absolute z-10"
              />
              <div className="w-full p-2 border rounded-md text-sm text-gray-500 flex items-center justify-between">
                {file ? file.name : 'Drop a file here or click'}
                <FaCloudUploadAlt className="text-gray-400" size={20} />
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-sm ${
                isLoading ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              <FaCloudUploadAlt size={16} />
              {isLoading ? 'Importing...' : 'Import Medicines'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImportMedicines;